import { useEffect, useRef } from 'react';

/**
 * Refetch a page's data whenever the tab regains visibility/focus.
 *
 * Why: browsers throttle BroadcastChannel events when tabs are backgrounded,
 * so the data-sync bus may not deliver updates from other tabs. When the user
 * comes BACK to this tab, we refresh proactively. Cheap insurance.
 *
 * Cooldown (default 5s) prevents thrashing if the user rapidly alt-tabs.
 *
 * @param {function} fetchFn   - the page's fetch callback (must be stable; wrap in useCallback)
 * @param {number}   cooldownMs- min ms between refetches (default 5000)
 */
export const useRefetchOnFocus = (fetchFn, cooldownMs = 5000) => {
  const lastFetchedRef = useRef(Date.now());
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchedRef.current < cooldownMs) return;
      lastFetchedRef.current = Date.now();
      try { fetchFn?.(); }
      catch (err) {
        // fetchFn is supplied by the caller — never crash on its failures,
        // but in dev surface the breakage so it isn't silently lost.
        if (process.env.NODE_ENV !== 'production') {
          try { console.debug('[useRefetchOnFocus]', err?.message || err); } catch (_) {}
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchFn, cooldownMs]);
};
